import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Save, X, MessageSquare, Lock } from "lucide-react";
import { API } from "./adminConfig";

const ChatRoomsTab = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'inactive'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: 0
  });
  const [submitting, setSubmitting] = useState(false);

  // Filter rooms by status
  const activeRooms = rooms.filter(room => room.active === true);
  const inactiveRooms = rooms.filter(room => room.active === false);
  const currentRooms = activeTab === 'active' ? activeRooms : inactiveRooms;

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/chat/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
      toast.error('Error loading chat rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingRoom) {
        await axios.put(`${API}/admin/chat/rooms/${editingRoom.id}`, formData);
        toast.success('Chat room updated successfully!');
      } else {
        await axios.post(`${API}/admin/chat/rooms`, formData);
        toast.success('Chat room created successfully!');
      }
      
      fetchRooms();
      resetForm();
    } catch (error) {
      console.error('Error saving chat room:', error);
      toast.error('Error saving chat room');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRoomStatus = async (roomId, newStatus) => {
    const action = newStatus ? 'activate' : 'archive';
    
    if (!window.confirm(`Are you sure you want to ${action} this chat room?`)) {
      return;
    }

    try {
      await axios.patch(`${API}/admin/chat/rooms/${roomId}/archive`);
      toast.success(`Chat room ${action}d successfully`);
      fetchRooms();
    } catch (error) {
      console.error(`Error ${action}ing chat room:`, error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          `Error ${action}ing chat room`;
      
      toast.error(`Failed to ${action} room: ${errorMessage}`);
    }
  };

  const deleteRoom = async (roomId, permanent = true) => {
    if (!window.confirm('Are you sure you want to permanently delete this chat room? This will also delete all messages in the room. This action cannot be undone.')) {
      return;
    }

    try {
      console.log(`Attempting to delete room: ${roomId}`);
      const response = await axios.delete(`${API}/admin/chat/rooms/${roomId}?permanent=${permanent}`);
      console.log('Delete room response:', response.data);
      toast.success('Chat room deleted permanently');
      fetchRooms(); // Refresh the list
    } catch (error) {
      console.error('Error deleting chat room:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Error deleting chat room';
      
      toast.error(`Failed to delete room: ${errorMessage}`);
    }
  };

  const editRoom = (room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      description: room.description,
      order: room.order || 0
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', order: 0 });
    setEditingRoom(null);
    setShowAddForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy mx-auto"></div>
        <span className="mt-2 text-burgundy">Loading chat rooms...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Chat Rooms Management</h2>
        <Button 
          onClick={() => setShowAddForm(true)} 
          className="bg-burgundy hover:bg-burgundy/80 text-cream"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Room
        </Button>
      </div>

      {/* Active/Inactive Archive Tabs */}
      <div className="flex space-x-1 bg-burgundy/5 p-1 rounded-lg max-w-md">
        <Button
          variant={activeTab === 'active' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('active')}
          className={`flex-1 ${
            activeTab === 'active' 
              ? 'bg-burgundy text-white hover:bg-burgundy/80' 
              : 'text-burgundy hover:bg-burgundy/10'
          }`}
        >
          Active Rooms
          {activeRooms.length > 0 && (
            <Badge className="ml-2 bg-green-500 text-white text-xs">
              {activeRooms.length}
            </Badge>
          )}
        </Button>
        <Button
          variant={activeTab === 'inactive' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('inactive')}
          className={`flex-1 ${
            activeTab === 'inactive' 
              ? 'bg-burgundy text-white hover:bg-burgundy/80' 
              : 'text-burgundy hover:bg-burgundy/10'
          }`}
        >
          Archived Rooms
          {inactiveRooms.length > 0 && (
            <Badge className="ml-2 bg-gray-500 text-white text-xs">
              {inactiveRooms.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Add/Edit Room Form */}
      {showAddForm && (
        <Card className="bg-white border-burgundy/20">
          <CardHeader>
            <CardTitle className="font-serif text-burgundy">
              {editingRoom ? 'Edit Chat Room' : 'Create New Chat Room'}
            </CardTitle>
            <CardDescription>
              {editingRoom ? 'Update the room details below' : 'Create a new room for community discussions'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Room Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Perimenopause Support"
                  className="border-burgundy/30 focus:border-gold"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Room Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe what this room is for..."
                  className="w-full p-3 border border-burgundy/30 rounded-md focus:border-gold"
                  rows={3}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Display Order *
                </label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  placeholder="0"
                  className="border-burgundy/30 focus:border-gold"
                  required
                />
                <p className="text-xs text-burgundy/60 mt-1">Lower numbers appear first</p>
              </div>
              
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-burgundy hover:bg-burgundy/80 text-cream"
                >
                  {submitting ? 'Saving...' : (editingRoom ? 'Update Room' : 'Create Room')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Rooms List */}
      <div className="grid gap-4">
        {currentRooms.length === 0 ? (
          <Card className="bg-white border-burgundy/20">
            <CardContent className="text-center py-8">
              <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-30 text-burgundy" />
              <p className="text-burgundy/60">
                {activeTab === 'active' 
                  ? 'No active chat rooms found. Create your first room!' 
                  : 'No archived chat rooms. Rooms you deactivate will appear here.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          currentRooms.map((room) => (
            <Card key={room.id} className={`bg-white border-burgundy/20 ${!room.active ? 'opacity-60' : ''}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-burgundy font-serif">{room.name}</h3>
                      <span className="px-2 py-1 bg-gold/20 text-gold text-xs rounded-full">
                        Order: {room.order || 0}
                      </span>
                      {!room.active && (
                        <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-burgundy/70 mb-3">{room.description}</p>
                    <div className="text-sm text-burgundy/50">
                      Created: {formatDate(room.created_at)} • Members: {room.member_count || 0}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => editRoom(room)}
                      className="text-burgundy border-burgundy hover:bg-burgundy/10"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleRoomStatus(room.id, !room.active)}
                      className={room.active 
                        ? "text-orange-600 border-orange-600 hover:bg-orange-50" 
                        : "text-green-600 border-green-600 hover:bg-green-50"
                      }
                    >
                      {room.active ? (
                        <>
                          <Archive className="h-3 w-3 mr-1" />
                          Archive
                        </>
                      ) : (
                        <>
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Activate
                        </>
                      )}
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteRoom(room.id)}
                      className="text-red-600 border-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Community Personas (Seed Users) Tab Component

export default ChatRoomsTab;
